import './processor.css';
import '../projectscontent.css';

import { GlowingText2em } from "../../../../components/glowingtextcomponet/glowingtext2em.js";
import { GlowingText1em } from "../../../../components/glowingtextcomponet/glowingtext1em.js";
import CodeBlock from '../../../../components/codeblock/codeblock.js';
import { Gallery } from "../../../../components/gallery/gallery.js";

// board photos: the ALU calculator running on the DE2-115
import boardAdd  from "./boardimages/alu_add.jpg";
import boardSub  from "./boardimages/alu_sub.jpg";
import boardMul  from "./boardimages/alu_mul.jpg";
import boardDiv  from "./boardimages/alu_div.jpg";
import boardSlt  from "./boardimages/alu_slt.jpg";
import boardSlt0 from "./boardimages/alu_slt0.jpg";
import boardBeqz from "./boardimages/alu_beqz.jpg";

// register file on the board
import regfileBoard1 from "./boardimages/regfile_1.jpg";
import regfileBoard2 from "./boardimages/regfile_2.jpg";

// simulation waveforms (PNG imports fine through webpack)
import aluWave       from "./waveimages/alu.png";
import waveRegfile   from "./waveimages/regfile.png";
import waveControl   from "./waveimages/control.png";
import wavePc        from "./waveimages/pc.png";
import waveImem      from "./waveimages/imem.png";
import waveDmem      from "./waveimages/dmem.png";
import waveSequencer from "./waveimages/sequencer.png";
import waveCpu       from "./waveimages/cpu.png";

// schematic captures (Logisim)
import datapath from "./schematicimages/datapath.jpg";
import registerfile from "./schematicimages/registerfile.jpg";
import controlunit from "./schematicimages/controlunit.jpg";
import alu from "./schematicimages/alu.jpg";
import sequencer from "./schematicimages/sequencer.jpg";

// RTL logic-circuit schematics — served from public/ (CRA's SVGR can't parse the netlistsvg output)
const aluRtl = process.env.PUBLIC_URL + "/processor/alu.svg";
const rtlUrl = (m) => process.env.PUBLIC_URL + "/processor/" + m + ".svg";

// control unit on the board: every opcode decoded live (photo per opcode, served from public/)
const ctlBoard = (h) => process.env.PUBLIC_URL + "/processor/board/ctl_" + h + ".jpg";
const CONTROL_BOARD = [
    ['0', 'addi', 'RegWrite + ALUsrc · ALU op 0 (add)'],
    ['1', 'subi', 'RegWrite + ALUsrc · ALU op 1 (sub)'],
    ['2', 'muli', 'RegWrite + ALUsrc · ALU op 2 (mul)'],
    ['3', 'divi', 'RegWrite + ALUsrc · ALU op 3 (div)'],
    ['4', 'add',  'RegWrite · ALU op 0 (add)'],
    ['5', 'sub',  'RegWrite · ALU op 1 (sub)'],
    ['6', 'mul',  'RegWrite · ALU op 2 (mul)'],
    ['7', 'div',  'RegWrite · ALU op 3 (div)'],
    ['8', 'slt',  'RegWrite · ALU op 4 (set-less-than)'],
    ['9', 'beqz', 'Branch + ALUadr · ALU op 5 (subtract for ZERO)'],
    ['A', '—',    'reserved — all control lines quiet'],
    ['B', '—',    'reserved — all control lines quiet'],
    ['C', 'sw',   'MemWrite + ALUadr'],
    ['D', 'lw',   'MemULA + ALUadr + RegWrite'],
    ['E', 'j',    'Jump'],
    ['F', '—',    'reserved when photographed — now decodes jr (added later)'],
].map(([h, mn, sig]) => ({
    file: ctlBoard(h),
    description: 'op ' + h + (mn === '—' ? '' : ' · ' + mn) + ' — ' + sig,
}));

// ---- Each module's hand-written SystemVerilog (shown in its subtopic) --------
const regfileVerilog = `module regfile #(
    parameter int WIDTH = 16,   // bits per register
    parameter int NREG  = 16,   // number of registers
    parameter int SEL   = 4     // selector width (2**SEL >= NREG)
) (
    input  logic             clk,      // write-back clock
    input  logic             rst,      // synchronous reset: clears all registers
    input  logic             we,       // write enable (RegWrite)
    input  logic [SEL-1:0]   rd,       // write-select  (destination register)
    input  logic [WIDTH-1:0] wdata,    // data to write back
    input  logic [SEL-1:0]   rx,       // read-select A
    input  logic [SEL-1:0]   ry,       // read-select B
    output logic [WIDTH-1:0] rx_data,  // value of register[rx]
    output logic [WIDTH-1:0] ry_data   // value of register[ry]
);
    // The storage: NREG registers of WIDTH bits each (an unpacked array).
    logic [WIDTH-1:0] regs [0:NREG-1];

    // ---- SEQUENTIAL: write happens only on the clock edge ----------------
    integer i;
    always_ff @(posedge clk) begin
        if (rst) begin
            for (i = 0; i < NREG; i = i + 1)
                regs[i] <= {WIDTH{1'b0}};
        end else if (we) begin
            regs[rd] <= wdata;          // store into the RD-selected register
        end
    end

    // ---- COMBINATIONAL: reads follow the selectors immediately -----------
    assign rx_data = regs[rx];
    assign ry_data = regs[ry];
endmodule`;

const controlVerilog = `module control (
    input  logic [3:0] op,        // 4-bit opcode of the current instruction
    output logic       jump,      // unconditional jump (j)
    output logic       branch,    // conditional branch (beqz)
    output logic       memwrite,  // write data memory (sw)
    output logic       memula,    // write-back value comes from data memory (lw)
    output logic       aluadr,    // route ALU output to the address / branch path
    output logic [2:0] ulaop,     // ALU operation select
    output logic       regwrite,  // WriteBack: write the result into register RD
    output logic       alusrc     // ALU 2nd operand = immediate (1) or register (0)
);
    // Opcode map (matches the Logisim design's UNIDADE DE CONTROLE).
    localparam logic [3:0] OP_ADDI=4'h0, OP_SUBI=4'h1, OP_MULI=4'h2, OP_DIVI=4'h3,
                           OP_ADD =4'h4, OP_SUB =4'h5, OP_MUL =4'h6, OP_DIV =4'h7,
                           OP_SLT =4'h8, OP_BEQZ=4'h9, OP_SW  =4'hC, OP_LW  =4'hD,
                           OP_J   =4'hE, OP_JR  =4'hF;   // 0xA, 0xB unused

    // ALU operation codes (mirror alu.sv ULAOP encoding).
    localparam logic [2:0] ULA_ADD=3'b000, ULA_SUB=3'b001, ULA_MUL=3'b010,
                           ULA_DIV=3'b011, ULA_SLT=3'b100, ULA_BEQZ=3'b101;

    always_comb begin
        // ---- DEFAULTS: every output gets a value first (no latches) -------
        jump=1'b0; branch=1'b0; memwrite=1'b0; memula=1'b0;
        aluadr=1'b0; ulaop=ULA_ADD; regwrite=1'b0; alusrc=1'b0;

        // ---- Per-opcode overrides: one arm == one row of the truth table --
        unique case (op)
            // Immediate ALU ops: 2nd operand = immediate, write RD
            OP_ADDI : begin ulaop=ULA_ADD; regwrite=1'b1; alusrc=1'b1; end
            OP_SUBI : begin ulaop=ULA_SUB; regwrite=1'b1; alusrc=1'b1; end
            OP_MULI : begin ulaop=ULA_MUL; regwrite=1'b1; alusrc=1'b1; end
            OP_DIVI : begin ulaop=ULA_DIV; regwrite=1'b1; alusrc=1'b1; end

            // Register ALU ops: 2nd operand = register, write RD
            OP_ADD  : begin ulaop=ULA_ADD; regwrite=1'b1; end
            OP_SUB  : begin ulaop=ULA_SUB; regwrite=1'b1; end
            OP_MUL  : begin ulaop=ULA_MUL; regwrite=1'b1; end
            OP_DIV  : begin ulaop=ULA_DIV; regwrite=1'b1; end
            OP_SLT  : begin ulaop=ULA_SLT; regwrite=1'b1; end

            // Control flow / memory
            OP_BEQZ : begin branch=1'b1;   aluadr=1'b1; ulaop=ULA_BEQZ; end
            OP_SW   : begin memwrite=1'b1; aluadr=1'b1; end
            OP_LW   : begin memula=1'b1;   aluadr=1'b1; regwrite=1'b1; end
            OP_J    : begin jump=1'b1; end               // PC = 12-bit target
            OP_JR   : begin jump=1'b1; branch=1'b1; end  // jump+branch pair -> PC = reg[RX]

            default : begin /* 0xA, 0xB: reserved -> all-zero defaults */ end
        endcase
    end
endmodule`;

const pcVerilog = `module pc #(
    parameter int W = 4          // PC width; 2**W = size of instruction memory
) (
    input  logic         clk,    // fetch clock
    input  logic         rst,    // synchronous reset: forces PC back to 0
    input  logic         en,     // advance enable: +1 during the PC-update phase
    input  logic         load,   // load target (jump / branch-taken); beats en
    input  logic [W-1:0] target, // branch/jump destination address
    output logic [W-1:0] pc_out  // current instruction address
);
    // SEQUENTIAL: the PC only changes on a rising clock edge.
    // The if / else-if chain encodes the priority  rst > load > en > (hold).
    always_ff @(posedge clk) begin
        if (rst)
            pc_out <= {W{1'b0}};        // synchronous clear to address 0
        else if (load)
            pc_out <= target;           // jump / taken branch: redirect fetch
        else if (en)
            pc_out <= pc_out + 1'b1;     // normal advance; wraps at 2**W
        // else: neither load nor en -> HOLD (no assignment => keep old value)
    end
endmodule`;

const imemVerilog = `module imem #(
    parameter int AW = 4,   // address width (PC bits). 2**AW = number of words
    parameter int DW = 16   // data width: bits per instruction word
) (
    input  logic [AW-1:0] addr,   // address from the PC
    output logic [DW-1:0] instr   // instruction word stored at that address
);
    // The storage: (1<<AW) words of DW bits each (an unpacked array).
    logic [DW-1:0] mem [0:(1<<AW)-1];

    // Initialize the ROM contents (simulation / FPGA power-on load).
    integer i;
    initial begin
        for (i = 0; i < (1<<AW); i = i + 1)   // start from an all-zero ROM
            mem[i] = {DW{1'b0}};

        mem[0] = 16'h0000;   // the 7-word demo program from the design brief
        mem[1] = 16'h0112;
        mem[2] = 16'h0221;
        mem[3] = 16'h8312;
        mem[4] = 16'h9032;
        mem[5] = 16'h0240;
        mem[6] = 16'he003;
        // mem[7]..mem[15] stay 0x0000; a real build could instead do:
        //     $readmemh("program.hex", mem);
    end

    // COMBINATIONAL (async) read: output follows the address immediately.
    assign instr = mem[addr];
endmodule`;

const dmemVerilog = `module dmem #(
    parameter int AW = 16,           // address width -> 2**AW words of storage
    parameter int DW = 16            // data width    -> bits per word
) (
    input  logic          clk,       // memory clock
    input  logic          we,        // write-enable (1 = store wdata this edge)
    input  logic [AW-1:0] addr,      // word address (for both read and write)
    input  logic [DW-1:0] wdata,     // data to store on a write (sw)
    output logic [DW-1:0] rdata      // data read back (valid ONE clock after addr)
);
    // 2**AW words of DW bits. For AW=16 that is 65,536 words -- must be block RAM.
    logic [DW-1:0] mem [0:(1<<AW)-1];

    // SEQUENTIAL: one clocked block does BOTH the write and the registered read.
    // This is the canonical pattern synthesis recognises as "block RAM".
    always_ff @(posedge clk) begin
        if (we)
            mem[addr] <= wdata;      // WRITE: store wdata (only when we is high)

        rdata <= mem[addr];          // READ : latch mem[addr] -> valid next cycle
    end
endmodule`;

const sequencerVerilog = `module sequencer (
    input  logic       clk,    // one phase advance per rising edge
    input  logic       rst,    // synchronous reset: reload phase = PC (00001)
    output logic [4:0] phase   // one-hot: [0]=PC [1]=Fetch/Decode [2]=Execute
                               //          [3]=Memory [4]=Write-back
);
    // SEQUENTIAL: the phase register advances once per clock edge.
    always_ff @(posedge clk) begin
        if (rst) begin
            phase <= 5'b00001;                 // start each instruction at PC
        end else if (phase == 5'b00000) begin
            phase <= 5'b00001;                 // self-correct an illegal all-zero
        end else begin
            phase <= {phase[3:0], phase[4]};   // rotate left: the ring counter
        end
    end
endmodule`;

// the fetch-stage integration: the PC's output feeds the instruction memory's address
const fetchVerilog = `// FETCH: wire the program counter to the instruction memory.
// The PC produces an address; the memory returns the instruction stored there.

logic [3:0]  pc_addr;   // where the PC is pointing
logic [15:0] instr;     // the instruction fetched from that address

pc #(.W(4)) u_pc (
    .clk    (clk),
    .rst    (rst),
    .en     (advance),  // step to the next instruction
    .load   (1'b0),     // fetch-only demo: no jumps yet
    .target (4'd0),
    .pc_out (pc_addr)   // the PC's output ...
);

imem #(.AW(4), .DW(16)) u_imem (
    .addr  (pc_addr),   // ... becomes the memory's address
    .instr (instr)      // the fetched instruction word
);`;

// the full integrated CPU: the sequencer conducts every block through 5 phases
const cpuVerilog = `// The 5-phase ring sequencer conducts the datapath; each block acts in its phase.
sequencer u_seq (.clk(clk), .rst(rst), .phase(phase));

// Program counter — commits in the write-back phase (P5): +1, or a taken target.
pc #(.W(PCW)) u_pc (
    .clk(clk), .rst(rst),
    .en(phase[4]), .load(phase[4] & take), .target(target), .pc_out(pc_out));

// Fetch — the PC addresses the instruction ROM (combinational read).
imem #(.AW(PCW), .DW(16)) u_imem (.addr(pc_out), .instr(instr));

// Decode — the control unit turns the opcode into every control line + ULAOP.
control u_ctrl (.op(op),
    .jump(jump), .branch(branch), .memwrite(memwrite), .memula(memula),
    .aluadr(aluadr), .ulaop(ulaop), .regwrite(regwrite), .alusrc(alusrc));

// Register file — two combinational reads, one clocked write (in P5).
regfile #(.WIDTH(16), .NREG(16), .SEL(4)) u_rf (
    .clk(clk), .rst(rst), .we(phase[4] & regwrite),
    .rd(rd), .wdata(wb_data), .rx(rx), .ry(ryimm),
    .rx_data(rx_data), .ry_data(ry_data));

// Execute — the ALU computes; result + ZERO flag are latched in P3.
alu #(.WIDTH(16)) u_alu (
    .a(rx_data), .b(alu_b), .ulaop(ulaop),
    .result(alu_result), .zero(alu_zero));

// Next PC (P5): pick the jump target, and whether to take it.
//   jr   (jump & branch) -> reg[RX]     (register-indirect jump)
//   j    (jump only)     -> 12-bit imm  (reaches 0-4095)
//   beqz (branch only)   -> 4-bit I     ; otherwise PC + 1
assign take   = jump | (branch & zero_reg);
assign target = (jump & branch) ? rx_data
              : jump            ? instr[11:0]
                                : ryimm;`;

// the remaining CPU modules: SystemVerilog + generated circuit + waveform, each
// with an intro (leading into the code) and a paragraph explaining each image.
const OTHER_MODULES = [
    { svg: 'regfile', name: 'Register file', wave: waveRegfile, code: regfileVerilog,
      blurb: `The register file is sixteen 16-bit registers with one write port and two read ports — the small memory that feeds operands into the ALU and catches its results. What makes it a good teaching piece is that it puts both kinds of logic side by side in one file: the two read ports are plain combinational assign statements whose outputs follow the selectors with no clock involved, while the single write port lives in an always_ff @(posedge clk) block that only commits a new value on the rising edge and remembers it afterward. The guiding question is simply whether the hardware has to remember something between clock edges: reading does not, so it is combinational; writing does, so it is sequential, uses the non-blocking <=, and clears all sixteen registers on a synchronous reset tested inside the clocked block.`,
      codePara: `Notice how the two read ports are plain assign statements while the single write lives in the clocked always_ff block — that one distinction is the whole design. Elaborated, it becomes the gate-level circuit below:`,
      circuitPara: `The elaborated circuit makes that split visible as structure: the write path becomes an address decoder driven by rd and we that steers wdata to exactly one register, feeding a bank of edge-triggered flip-flops — sixteen registers of sixteen bits is 256 flip-flops, the only stateful elements in the design. Each read port, by contrast, compiles to a wide multiplexer that selects one of the sixteen stored words purely from its selector, so the two read outputs are combinational fan-out from the same flip-flop array with no clock in the path. Seeing the decoder-plus-registers on one side and the two selection multiplexers on the other is exactly the always_ff-versus-assign distinction rendered as gates and storage.`,
      wavePara: `The waveform tracks the testbench's escalating checks: the synchronous reset is held across a rising edge and every register reads back as zero on both ports, then a directed write to register 5 appears at the output only after the clock edge that commits it. A deliberate we-low cycle shows the write being gated — the target register keeps its old value despite fresh data on wdata — and driving the two read selectors to different registers confirms the ports are independent, before a random write-all followed by a read-back sweep exercises the whole bank against the reference model.`,
      synth: '368 logic elements, 256 registers.',
      formal: 'a k-induction proof (Yosys SAT) shows the read/write contract holds for all time — after a reset every register reads back zero, and any value written is returned by the next read of that register — verified for every register and every value at once, not sampled.',
      boardIntro: 'Brought up on the DE2-115 as an interactive demo — dial a value and a register, write, then read it back:',
      board: [
          { file: regfileBoard1, description: 'On the DE2-115: a value written to a register and read back on the displays' },
          { file: regfileBoard2, description: 'A different register holding its own value — the 16 registers are independent' },
      ] },
    { svg: 'control', name: 'Control unit', wave: waveControl, code: controlVerilog,
      blurb: `The control unit is a purely combinational opcode decoder: it reads the 4-bit opcode and, through a single always_comb with a case whose arms are the rows of the instruction truth table, drives every datapath control line plus the 3-bit ulaop that tells the ALU which operation to perform. The teaching point it exists to demonstrate is latch avoidance in combinational logic — every output is assigned a default of zero at the very top of the block before any case arm runs, so no execution path can leave an output unspecified and quietly force the synthesizer to infer a latch where only flat logic belongs. Each arm then only overrides the few lines that must go high, so the code reads like the datasheet it implements.`,
      codePara: `Notice the block assigns every output a default of zero before the case runs, so no execution path can leave one unset (and infer a latch). Elaborated, that truth table becomes the flat decode circuit below:`,
      circuitPara: `Because the description carries no clock and no state, the elaborated circuit is a flat sheet of decode logic: the four opcode bits fan straight out through gates into the control outputs, without a single flip-flop, register, or memory element anywhere in the netlist. That absence is the visual confirmation that defaulting every output really did keep the block combinational — a decoder is nothing more than a truth table cast into gates, and the drawn logic looks exactly like that lookup rather than anything sequential.`,
      wavePara: `Since the decoder has only sixteen possible opcodes, the testbench forgoes random stimulus entirely and instead sweeps the input exhaustively from 0x0 through 0xF, packing the outputs into one bus and checking it against a hand-written golden truth table at each value. The waveform advances one opcode per step, showing the control lines snap to a new column with every code — the reserved encodings 0xA and 0xB correctly falling through to the all-zero default, while 0xF (jr) is the one opcode that raises jump and branch together, the pair the datapath reads as a register-indirect jump.`,
      synth: '10 logic elements, no registers (pure combinational).',
      formal: 'a SAT proof confirms every control line is asserted for exactly the right opcodes — all 16, checked exhaustively.',
      boardIntro: 'Brought up on the DE2-115 as a live decoder — dial the opcode on the switches (HEX7), read the ALU operation it selects on HEX5, and watch each green LED light for one asserted control line. Here is the whole control table, opcode by opcode, on real hardware:',
      board: CONTROL_BOARD },
    { svg: 'pc', name: 'Program counter', wave: wavePc, code: pcVerilog,
      blurb: `With the program counter the contrast with the ALU becomes the whole point: where the ALU's output follows its inputs instantly, the PC must remember an address between clock edges, so it lives in an always_ff block and updates with the non-blocking assignment. Its behaviour is an if / else-if priority chain — reset, then load, then enable, and otherwise hold — where only the first true branch fires each edge, which is precisely how a taken branch overriding the normal +1 advance is expressed in hardware. The reset is tested inside the clocked block, making it synchronous (the safe FPGA default), and the advance is a plain unsigned increment that wraps back to zero at the top of the 4-bit range for free.`,
      codePara: `Notice the priority if / else-if chain — reset, then load, then enable, and otherwise hold — where only the first true branch fires each edge. Elaborated, it becomes the small register-plus-multiplexer circuit below:`,
      circuitPara: `Elaborated, the description collapses to what it really is: a 4-bit register whose next-value input comes from a small multiplexer, selecting between the load target, the current value plus one, and the current value fed straight back. That feedback path is the hold case made visible — the omitted else in the code becomes a wire that reloads the flip-flops with their own output, so nothing about holding a value needs extra state, only a mux input. The priority of load over enable shows up as the ordering of the mux's select logic rather than as any separate arbitration circuit.`,
      wavePara: `The waveform walks the PC through each behaviour in turn: a synchronous clear to zero, a run of enabled edges stepping 1, 2, 3 and up, a load that snaps the address to 7, and the deciding case where load and enable are asserted together and the PC takes the target instead of incrementing. It also drives the counter to its top value and shows the next enabled edge rolling it over to zero, confirming the natural wrap, before a long pseudo-random sweep of all four controls tracks a reference model with no mismatches.`,
      synth: '8 logic elements, 4 registers.',
      formal: 'a k-induction proof (Yosys SAT) shows the priority holds every cycle for every input — reset beats load beats +1 beats hold — so a taken branch always overrides the normal increment.' },
    { svg: 'imem', name: 'Instruction memory', wave: waveImem, code: imemVerilog,
      blurb: `The instruction memory is a 16-word by 16-bit ROM that acts as the processor's L1-I: the program counter presents an address and the module hands back the instruction word stored there. Its central teaching point is the contrast with the register file — there is no clk, no write enable, and no write logic anywhere, so the store is read-only and its single read is asynchronous, a continuous assign whose output tracks the address the instant it changes. The fixed contents are laid down once in an initial block that clears all sixteen words and then writes the seven-word demo program, an approach that swaps cleanly for a $readmemh hex load and, on the FPGA, becomes the ROM's power-on initialization.`,
      codePara: `Notice there is no clock and no write port — the read is a single continuous assign, and the contents are fixed in an initial block. Elaborated, the ROM collapses into the logic below:`,
      circuitPara: `Because the sixteen words are constants fixed at elaboration and the read is combinational, there is nothing to store at run time, so synthesis does not infer a RAM block: it collapses the whole ROM into a small cloud of logic, essentially an address-selected multiplexer over hard-wired bit patterns with the many all-zero words pruned away. The result is a circuit with no flip-flops at all — the address bits fan straight into gates that reconstruct each stored word — which is the visible payoff of choosing an assign over a clocked always_ff for the read path.`,
      wavePara: `The testbench walks the address across all sixteen locations, pausing only a settle delay rather than a clock edge between reads, and compares each output against an in-testbench reference array. The waveform shows the instruction word changing purely as a function of the address — the seven demo-program words at locations 0 through 6 and 0x0000 for every slot beyond — with no clock signal anywhere in sight, which is exactly what an asynchronous read should look like.`,
      synth: '10 logic elements.',
      formal: 'a SAT proof confirms the ROM returns exactly the intended program word at every one of its 16 addresses — checked exhaustively, and that the read is a pure function of the address.' },
    { svg: 'dmem', name: 'Data memory', wave: waveDmem, code: dmemVerilog,
      blurb: `The data memory is the read/write RAM that the load and store instructions talk to: sw writes a word to mem[addr] on a clock edge when write-enable is high, and lw reads one back. The subtle part is that the read is clocked too — rdata <= mem[addr] inside a single always_ff, rather than a combinational assign — which deliberately buys a one-cycle read latency so the whole array maps onto the Cyclone IV E's dedicated M9K block RAM instead of being built from thousands of logic registers. It is the opposite trade-off to the register file's same-cycle combinational read: that memory is tiny and time-critical for the ALU, this one is huge and can tolerate a pipeline bubble.`,
      codePara: `Notice the read is inside the clocked block (rdata <= mem[addr]), not a combinational assign — that single choice is what lets the array map onto a block RAM. Elaborated, it becomes the memory primitive below:`,
      circuitPara: `Because the write and the registered read share one clocked block, the elaborated circuit is not a sea of gates but a recognized memory primitive: a write port gated by we and a read port whose output is captured in a register, with no combinational path running from addr straight to rdata. That registered output is exactly the shape the block RAM hardware expects, which is why a single always_ff written this way is the canonical pattern synthesis tools infer as on-chip RAM rather than distributed logic.`,
      wavePara: `The testbench shrinks the array to 256 words for speed but exercises the same RTL, and the waveform makes the one-cycle latency visible — the read data settles on the clock after the address is applied, not the same edge. It also captures the same-address collision case: when 0x5555 is written and address 5 is read on the same edge, rdata comes back as the old 0xABCD, because the non-blocking assignments sample mem[addr] before the write commits — read-before-write behaviour — and a fresh read on the next cycle then returns the new value.`,
      closingPara: `It is worth stepping back to see how close this is to the instruction memory: both are the exact same primitive — an addressable array of words, mem[addr] — and in a von Neumann machine they would literally be one block. What separates them here is not the storage but the access. The instruction memory is a read-only ROM read combinationally, so the word appears the instant the address changes; the data memory is read/write and its read is registered, so the word appears one clock later — the very latency that lets this much larger array map onto the FPGA's block RAM. Because of that kinship, the data memory needs no board demo of its own: reading a word at an address behaves just like the instruction memory shown above, and only the write path and the one-cycle read delay are genuinely new.`,
      synth: '112 logic elements, 3 registers, 1,048,576 memory bits (block RAM).',
      formal: 'a k-induction proof (Yosys SAT) shows the registered read always returns the last value written to that address, one cycle later — verified for every address and every value, not sampled.' },
    { svg: 'sequencer', name: 'Clock sequencer', wave: waveSequencer, code: sequencerVerilog,
      blurb: `The clock sequencer's whole job is to remember which step the machine is on, so — unlike the purely combinational ALU — this description is sequential and lives entirely inside an always_ff block clocked on the rising edge. It is a one-hot ring counter: five flip-flops hold the machine phase with exactly one bit set, and each clock rotates that bit one position to the left so the active phase marches PC, Fetch/Decode, Execute, Memory, Write-back, then wraps back to PC. Because the phase register is the only state carried across edges it is updated with the non-blocking operator, and its reset is synchronous — tested on the clock edge rather than asynchronously — loading 00001 so the machine always begins a fresh instruction at PC.`,
      codePara: `Notice the whole update rule is just a rotate — {phase[3:0], phase[4]} — with a synchronous reset to 00001 and a cheap all-zero self-correcting guard. Elaborated, it becomes the ring of flip-flops below:`,
      circuitPara: `What the elaborated netlist shows here is not arithmetic but five flip-flops wired in a loop: each phase bit's output feeds the next bit's input and the top bit wraps back into the bottom, which is the whole ring. The synchronous reset and the cheap all-zero self-correcting guard fold into a small multiplexer on the data path that snaps 00001 back in when needed, and because the encoding is one-hot, every phase bit leaves the register already usable as an enable — there is no decoder standing between this counter and the datapath it will eventually sequence.`,
      wavePara: `The waveform walks the ring for roughly fifteen edges — three full laps of the five phases — which demonstrates not just that the order is right but that it is genuinely cyclic, with 10000 wrapping cleanly back to 00001 each lap rather than stalling or latching a dead code. Every sampled cycle is also checked to be truly one-hot with $countones, the property that lets each phase bit be trusted as a standalone enable, and the run confirms that reset lands the counter on 00001 before the free-run ever begins.`,
      synth: '6 logic elements, 5 registers.',
      formal: 'a k-induction proof (Yosys SAT) shows the phase register is always one-hot — the ring can never wander into an illegal multi-hot state and stall — for all time and every input, so each phase bit can be trusted as a standalone enable.',
      videoIntro: `Brought up on the DE2-115 as a marching light: the five one-hot phase bits drive five LEDs, so a single dot of light steps around the ring while the display shows the active phase number (1–5). A switch runs it automatically or lets you single-step one phase per button press.`,
      video: process.env.PUBLIC_URL + '/processor/sequencer_march.mp4',
      videoCaption: `The sequencer on the DE2-115 — one lit LED marching through the five phases (caught here at phase 2, Fetch/Decode)`,
      videoPara: `In the clip the light does exactly what the code promises: the single lit LED advances one position each tick — phase 1 (PC) to 2 (Fetch/Decode) to 3 (Execute) to 4 (Memory) to 5 (Write-back) — and then wraps straight back to phase 1, with the display tracking the number the whole way. Because the encoding is one-hot, that moving dot is not just a picture of the state: each lit bit is already the enable line for its phase, ready to switch on the matching part of the datapath once the full processor is wired together.` },
];

// standalone synthesis on the DE2-115's EP4CE115F29C7  [module, logic elements, registers, memory bits, DSP 9-bit]
const SYNTH = [
    ['ALU',                '387', '0',   '—',         '2'],
    ['Register file',      '368', '256', '—',         '—'],
    ['Control unit',       '10',  '0',   '—',         '—'],
    ['Program counter',    '8',   '4',   '—',         '—'],
    ['Instruction memory', '10',  '0',   '—',         '—'],
    ['Data memory',        '112', '3',   '1,048,576', '—'],
    ['Clock sequencer',    '6',   '5',   '—',         '—'],
];

// the actual output of tools/run_formal.ps1 — every leaf module's machine-checked verdict
const formalSuiteOutput = `$ powershell -File tools/run_formal.ps1

alu_props        -> PROVED (exhaustive, all inputs)
control_props    -> PROVED (exhaustive, all inputs)
imem_props       -> PROVED (exhaustive, all inputs)
pc_props         -> PROVED (unbounded, k-induction)
regfile_props    -> PROVED (unbounded, k-induction)
dmem_props       -> PROVED (unbounded, k-induction)
sequencer_props  -> PROVED (unbounded, k-induction)`;

// a raw excerpt of Yosys's SAT engine proving the sequencer's one-hot invariant by
// k-induction: it tries length 1, the step is not yet provable, it lengthens to 2, and closes
const formalInductionLog = `** Trying induction with length 1 **
Base case for induction length 1 proven.
[induction step 1] Solving problem with 493 variables and 1336 clauses..
Induction step failed. Incrementing induction length.
** Trying induction with length 2 **
Base case for induction length 2 proven.
[induction step 2] Solving problem with 799 variables and 2201 clauses..
Induction step proven: SUCCESS!`;

// the program the CPU runs to COMPUTE the bounce and drive the VGA display
const vgaProgram = `        addi r2, r0, 4     ; dx = 4  (the step)
        addi r5, r0, 1     ; r5 = the memory-mapped video address
        addi r7, r0, 9     ;
        muli r7, r7, 8     ;
        muli r7, r7, 8     ; r7 = 576  (right wall = 640 - 64)
loop:   add  r1, r1, r2    ; x += dx
        sw   r5, r1        ; store x to the video register
        sub  r9, r1, r7    ; r9 = x - XMAX
        beqz r9, flip      ; hit the right wall? -> reverse
        beqz r1, flip      ; hit the left wall (x==0)? -> reverse
        j    loop          ; else keep going
flip:   sub  r2, r0, r2    ; dx = -dx   (0 - dx)
        j    loop`;

// the hardware no longer computes anything -- it just draws the square where
// the CPU put it (from vga_square.sv)
const vgaRender = `// pos_x is whatever X the CPU last stored; the vertical position
// is fixed at the screen centre. No arithmetic here -- just a test.
in_box = (x >= pos_x) && (x < pos_x + SIZE) &&
         (y >= BOX_Y) && (y < BOX_Y + SIZE);
{vga_r, vga_g, vga_b} = in_box ? 24'h000000 : 24'hFFFFFF;  // black on white`;

// the tile "GPU": which cell does this pixel fall in, and what colour is it?
const gpuRender = `// For every pixel the raster draws, find which tile cell it lands in
// (16x16-px cells make the divide a bit-slice), read that cell's colour
// index from the tile framebuffer, and paint it. The CPU "draws" purely by
// storing a colour into a cell -- a real framebuffer, at tile resolution.
col   = x[9:4];                     // x / 16  ->  column 0..39
row   = y[9:4];                     // y / 16  ->  row    0..29
raddr = row * COLS + col;           // the cell this pixel belongs to

// dual-port RAM: the CPU writes cells on its clock, the video reads on the
// pixel clock. The REGISTERED read is what maps it onto on-chip block RAM.
tileram u_fb (
    .clk_w(cpu_clk), .we(store),  .addr_w(cell),  .data_w(colour),
    .clk_r(pix_clk), .addr_r(raddr), .data_r(cell_val));

{vga_r, vga_g, vga_b} = (cell_val == 1) ? 24'h000000    // ball = black
                                        : 24'hFFFFFF;   // background = white`;

// the per-frame gravity physics, done entirely in the CPU's own instructions
const gravAlgo = `; every frame, in the CPU's own integer arithmetic:
vy     += G                        ; gravity  (G = 1)
fine_y += vy                       ; move     (fine_y is in 1/8-cell units)

hit     = (fine_y >= FLOOR)        ; reached the floor?          -> 0 or 1
fine_y  = hit ? FLOOR : fine_y     ; clamp exactly onto the floor
vy      = hit ? -E    : vy         ; bounce: relaunch upward with energy E
E       = E - hit                  ; ...spending one unit of energy per bounce

spent   = (E == 0)                 ; all the energy gone?        -> 0 or 1
E, fine_y, vy = spent ? (E_MAX, 0, 0)      ; reset: a fresh drop from the top
                      : (E, fine_y, vy)

row = fine_y / 8                   ; which tile row to light this frame`;

// the actual program the CPU runs to bounce the ball (the real grav1 assembly)
const gravProgramFull = `; ---- setup: build the constants (the immediate is only 4 bits) + clear state ----
        addi r1, r0, 8
        muli r1, r1, 5      ; r1 = COLS = 40      (grid is 40 x 30 cells)
        addi r2, r0, 4
        muli r2, r2, 5      ; r2 = col = 20       (the ball's fixed column)
        addi r3, r0, 4
        muli r3, r3, 7      ; r3 = 28             (the floor row)
        muli r3, r3, 8      ; r3 = FLOOR = 224    (floor in 1/8-cell units)
        addi r4, r0, 7
        muli r4, r4, 3      ; r4 = E_MAX = 21     (starting bounce energy)
        addi r5, r0, 1      ; r5 = 1  (gravity G, the constant 1, and the colour index)
        addi r6, r0, 0      ; r6 = fine_y = 0     (vertical position, 1/8-cell units)
        addi r7, r0, 0      ; r7 = vy = 0         (vertical velocity)
        addi r8, r0, 0      ; r8 = prev_row = 0
        add  r9, r4, r0     ; r9 = E = E_MAX      (live energy)
        mul  r14, r1, r1    ; r14 = SCRATCH = 40*40 = 1600  (an off-screen cell)
; ---- frame loop ----
loop:   add  r7, r7, r5     ; vy += G             (gravity, before the move)
        add  r6, r6, r7     ; fine_y += vy
        slt  r10, r6, r3    ; below = fine_y < FLOOR
        sub  r11, r5, r10   ; hit = 1 - below     (1 exactly on/under the floor)
        sub  r12, r6, r3    ; t = fine_y - FLOOR
        mul  r12, r11, r12  ; t = hit * t
        sub  r6, r6, r12    ; fine_y = FLOOR on a hit    (clamp onto the floor)
        add  r12, r7, r9    ; t = vy + E
        mul  r12, r11, r12  ; t = hit * t
        sub  r7, r7, r12    ; vy = -E on a hit           (relaunch with energy E)
        sub  r9, r9, r11    ; E -= hit                   (lose one unit per bounce)
        slt  r12, r0, r9    ; ezero  = (0 < E)           (1 while energy remains)
        sub  r13, r5, r12   ; atzero = 1 - ezero         (1 exactly when E hit 0)
        mul  r15, r13, r4   ; t = atzero * E_MAX
        add  r9, r9, r15    ; E = E_MAX again on reset
        mul  r6, r6, r12    ; fine_y = 0 on reset        (fresh drop from the top)
        mul  r7, r7, r12    ; vy = 0 on reset
        divi r12, r6, 8     ; row = fine_y / 8           (the tile row to light)
        mul  r10, r12, r1   ; newCell = row * COLS
        add  r10, r10, r2   ;         + col
        mul  r11, r8, r1    ; oldCell = prev_row * COLS
        add  r11, r11, r2   ;         + col
        slt  r13, r12, r8   ; row < prev_row ?
        slt  r15, r8, r12   ; prev_row < row ?
        add  r13, r13, r15  ; moved = (row != prev_row)  -> 0 or 1
        sub  r15, r11, r14  ; d = oldCell - SCRATCH
        mul  r15, r13, r15  ; d = moved * d
        add  r15, r14, r15  ; erase = SCRATCH + moved*(oldCell - SCRATCH)
        sw   r10, r5        ; DRAW  new cell = colour    (ball lit at the new cell)
        sw   r15, r0        ; ERASE old cell, or the off-screen scratch if unmoved
        add  r8, r12, r0    ; prev_row = row
        j    loop           ; next frame`;

const aluVerilog = `module alu #(parameter int WIDTH = 16) (
    input  logic [WIDTH-1:0] a, b,      // Dado1, Dado2
    input  logic [2:0]       ulaop,     // operation select
    output logic [WIDTH-1:0] result,    // ULA RESULT
    output logic             zero       // ZERO flag
);
    always_comb begin
        unique case (ulaop)
            3'b000 : result = a + b;                     // add / addi
            3'b001 : result = a - b;                     // sub / subi
            3'b101 : result = a - b;                     // beqz: subtract; ZERO flag branches
            3'b010 : result = a * b;                     // mul / muli
            3'b011 : result = (b == 0) ? '0
                                       : $signed(a) / $signed(b);  // div
            3'b100 : result = ($signed(a) < $signed(b))
                                       ? 16'd1 : 16'd0;  // set-less-than
            default: result = '0;
        endcase
    end

    assign zero = (result == 0);
endmodule`;

const ISA = [
    ['0x0', 'addi', 'RD, RX, I',    'RD = RX + I'],
    ['0x1', 'subi', 'RD, RX, I',    'RD = RX − I'],
    ['0x2', 'muli', 'RD, RX, I',    'RD = RX × I'],
    ['0x3', 'divi', 'RD, RX, I',    'RD = RX ÷ I'],
    ['0x4', 'add',  'RD, RX, RY',   'RD = RX + RY'],
    ['0x5', 'sub',  'RD, RX, RY',   'RD = RX − RY'],
    ['0x6', 'mul',  'RD, RX, RY',   'RD = RX × RY'],
    ['0x7', 'div',  'RD, RX, RY',   'RD = RX ÷ RY'],
    ['0x8', 'slt',  'RD, RX, RY',   'RD = (RX < RY) ? 1 : 0'],
    ['0x9', 'beqz', 'RX, target',   'if (RX == 0) jump to target'],
    ['0xA', '(reserved)', '—',      'free opcode for future use'],
    ['0xB', '(reserved)', '—',      'free opcode for future use'],
    ['0xC', 'sw',   'Radd, RData',  'DataMem[Radd] = RData'],
    ['0xD', 'lw',   'RD, Radd',     'RD = DataMem[Radd]'],
    ['0xE', 'j',    'target',       'jump to a 12-bit target (PC = target)'],
    ['0xF', 'jr',   'RX',           'jump to the address in RX (PC = RX)'],
];

const PHASES = [
    ['1', 'Program counter', 'The program counter presents the current address to the instruction memory.'],
    ['2', 'Fetch & decode',  'The instruction is read; the control unit decodes the opcode and the register file reads RX and RY.'],
    ['3', 'Execute',         'The ALU computes and its result is latched; the ZERO flag is produced.'],
    ['4', 'Memory',          'The data memory is read (lw) or written (sw).'],
    ['5', 'Write-back',      'The result is written to the destination register and the program counter is updated.'],
];

// Control word per opcode, as decoded by the control unit.
// [hex, mnem, Jump, Branch, MemWrite, MemULA, ALUadr, ULAop, WriteBack, ALUsrc, format]
// jr (0xF) asserts Jump AND Branch together -- a pair no other opcode uses -- and
// the datapath reads that pair as "take the jump target from register RX".
const CONTROL = [
    ['0x0', 'addi', '0', '0', '0', '0', '0', '000', '1', '1', 'OP RD RX I'],
    ['0x1', 'subi', '0', '0', '0', '0', '0', '001', '1', '1', 'OP RD RX I'],
    ['0x2', 'muli', '0', '0', '0', '0', '0', '010', '1', '1', 'OP RD RX I'],
    ['0x3', 'divi', '0', '0', '0', '0', '0', '011', '1', '1', 'OP RD RX I'],
    ['0x4', 'add',  '0', '0', '0', '0', '0', '000', '1', '0', 'OP RD RX RY'],
    ['0x5', 'sub',  '0', '0', '0', '0', '0', '001', '1', '0', 'OP RD RX RY'],
    ['0x6', 'mul',  '0', '0', '0', '0', '0', '010', '1', '0', 'OP RD RX RY'],
    ['0x7', 'div',  '0', '0', '0', '0', '0', '011', '1', '0', 'OP RD RX RY'],
    ['0x8', 'slt',  '0', '0', '0', '0', '0', '100', '1', '0', 'OP RD RX RY'],
    ['0x9', 'beqz', '0', '1', '0', '0', '1', '101', '0', '0', 'OP - RX I(target)'],
    ['0xA', '(reserved)', '-', '-', '-', '-', '-', '-', '-', '-', 'free opcode for future use'],
    ['0xB', '(reserved)', '-', '-', '-', '-', '-', '-', '-', '-', 'free opcode for future use'],
    ['0xC', 'sw',   '0', '0', '1', '0', '1', '000', '0', '0', 'OP - Radd RData'],
    ['0xD', 'lw',   '0', '0', '0', '1', '1', '000', '1', '0', 'OP RD Radd -'],
    ['0xE', 'j',    '1', '0', '0', '0', '0', '000', '0', '0', 'OP - - target(12-bit)'],
    ['0xF', 'jr',   '1', '1', '0', '0', '0', '000', '0', '0', 'OP - RX -'],
];

const CONTROL_COLS = ['Jump', 'Branch', 'Mem Write', 'Mem ULA', 'ALU adr', 'ULA op', 'Write Back', 'ALU src'];

const aluBoardImages = [
    { file: boardAdd,  description: 'ADD (op 0): A + B = 0004' },
    { file: boardSub,  description: 'SUB (op 1): A - B = 0002' },
    { file: boardMul,  description: 'MUL (op 2): A x B = 0004' },
    { file: boardDiv,  description: 'DIV (op 3): A / B = 0001' },
    { file: boardSlt,  description: 'SLT (op 4): A < B  ->  0001 (true)' },
    { file: boardSlt0, description: 'SLT (op 4): A < B  ->  0000 (false)' },
    { file: boardBeqz, description: 'SUB (op 5, beqz): A - B = 0000 (ZERO flag on)' },
];

// a small row of tool "chips" shown under a section heading, naming what built that stage
const ToolChips = ({ tools }) => (
    <div className='proc-tools'>
        <span className='proc-toolslabel'>Built with</span>
        {tools.map((t) => <span key={t} className='proc-tool'>{t}</span>)}
    </div>
);

// source-of-truth links: every code block points at its real file on GitHub
const REPO = 'https://github.com/GitScrider/16bits-processor/blob/main/';
const GitLink = ({ file }) => (
    <a className='proc-gitlink' href={REPO + file} target='_blank' rel='noreferrer'>
        {'\u{1F517} ' + file + ' on GitHub ↗'}
    </a>
);

// download links for the Logisim design files, served from public/
const DownloadLink = ({ file, label }) => (
    <a className='proc-download' href={process.env.PUBLIC_URL + '/processor/' + file} download>
        {'⬇ ' + label}
    </a>
);

export const Processor = () => (
    <div className='content'>

        <GlowingText2em content='16-bit RISC Processor' />

        <p className='projecttext'>
            A processor is the component that fetches instructions from memory, decodes them and
            executes them, coordinating arithmetic, data movement and program flow. Understanding
            how a CPU works on the inside is one of the most rewarding exercises in computer
            engineering — and the best way to understand it is to build one. This project is a
            16-bit RISC processor, similar in spirit to the MIPS architecture, designed and
            simulated entirely from logic blocks in Logisim, and now being re-implemented in
            synthesizable SystemVerilog to run on a real FPGA, the Altera/Intel DE2-115.
            <br></br>
            Every block — the program counter, the instruction and data memories, the register
            file, the control unit, the arithmetic-logic unit and the clock sequencer — was built
            and wired by hand, giving full visibility into how a computer runs a program at the
            level of gates and buses. The design uses 16-bit words, 16 general-purpose registers and
            a single fixed 16-bit instruction format, and it executes each instruction in a
            multicycle fashion across five machine phases.
        </p>

        <GlowingText2em content='Architecture' />

        <p className='projecttext'>
            The datapath follows the classic flow of a multicycle machine: the program counter
            addresses the instruction memory, the fetched instruction is decoded by the control
            unit while the register file reads its operands, the arithmetic-logic unit executes the
            operation, the data memory is accessed for loads and stores, and the result is finally
            written back to a register. In parallel, a next-address multiplexer decides where the
            program counter goes next: the usual PC + 1, an unconditional jump target, or a
            conditional branch target.
        </p>

        <img className='singleimage' src={datapath} alt='Top-level datapath of the processor' />
        <div className='singleimagedescription'>Top-level datapath (Logisim)</div>
        <div className='proc-downloads'>
            <DownloadLink file='Processor.circ' label='Logisim circuit (Processor.circ)' />
            <DownloadLink file='loop.mem' label='Demo program (loop.mem)' />
        </div>

        <p className='projecttext'>
            Every instruction is 16 bits wide and is split by a bus splitter into four 4-bit fields.
            The first field is the operation code; the next two select source/destination registers;
            the last is either a second source register or a 4-bit immediate that doubles as a
            branch target. The unconditional jump (j) is the exception: it needs no registers, so all
            twelve non-opcode bits become one wide address — and jr instead takes its target from
            register RX.
        </p>

        <div className='proc-instrformat'>
            <div className='proc-field'><span className='proc-fieldname'>OP</span><span className='proc-fieldbits'>4 bits</span></div>
            <div className='proc-field'><span className='proc-fieldname'>RD</span><span className='proc-fieldbits'>4 bits</span></div>
            <div className='proc-field'><span className='proc-fieldname'>RX</span><span className='proc-fieldbits'>4 bits</span></div>
            <div className='proc-field'><span className='proc-fieldname'>RY / I</span><span className='proc-fieldbits'>4 bits</span></div>
        </div>

        <p className='projecttext'>
            Because the opcode is 4 bits, the machine has room for up to 16 (2⁴) instructions, of
            which 14 are currently defined (0xA and 0xB are left reserved for future use). The 4-bit
            register selectors address 16 registers. A 4-bit immediate keeps ordinary values and
            beqz targets in the range 0–15, but the unconditional jump widens its target to all 12
            non-opcode bits (0–4095), and jr can jump anywhere a 16-bit register points. The base
            instruction memory holds 16 words of 16 bits — the program counter widens to reach larger
            programs — and the data memory holds up to 65,536 (2¹⁶) words of 16 bits.
        </p>

        <div className="subtext"><GlowingText1em content='Register file' /></div>
        <p className='projecttext'>
            The register file holds the sixteen 16-bit general-purpose registers, built as a 4 × 4
            grid of 16-bit registers. A write demultiplexer routes the write-back value to the register
            selected by the RD field, while two read multiplexers expose the registers selected by
            RX and RY on the two operand buses. Reading and writing are governed by separate clock
            strobes, so an operand can be read early in the instruction and the result written back
            at the end.
        </p>
        <img className='singleimage' src={registerfile} alt='Register file internals' />
        <div className='singleimagedescription'>Register file — 16 × 16-bit (Banco de Registradores)</div>

        <div className="subtext"><GlowingText1em content='Control unit' /></div>
        <p className='projecttext'>
            The control unit turns the 4-bit opcode into the signals that steer the whole datapath:
            whether the second ALU operand is a register or the immediate, whether a register is
            written, whether memory is read or written, and whether the program counter should
            branch or jump — plus a 3-bit code selecting the ALU operation. It is built from a
            decoder of AND gates whose outputs are OR-combined into each individual control line.
        </p>
        <img className='singleimage' src={controlunit} alt='Control unit' />
        <div className='singleimagedescription'>Control unit — opcode decoder (Unidade de Controle)</div>

        <div className="subtext"><GlowingText1em content='Arithmetic-logic unit (ALU)' /></div>
        <p className='projecttext'>
            The ALU performs addition, subtraction, multiplication, division and a
            set-less-than comparison, chosen by the 3-bit operation code coming from the control
            unit. It outputs the 16-bit result and a ZERO flag, which the branch logic uses to
            decide a taken branch. In Logisim the multiply and divide blocks resolve in a single
            simulation step; on real hardware those operations need extra care, which is one of the
            challenges of the FPGA port described further down.
        </p>
        <img className='singleimage' src={alu} alt='Arithmetic-logic unit' />
        <div className='singleimagedescription'>ALU — add / sub / mul / div / slt (ULA)</div>

        <div className="subtext"><GlowingText1em content='The five-phase clock sequencer' /></div>
        <p className='projecttext'>
            Rather than trying to complete an instruction in a single clock, the processor is
            multicycle: a ring of five flip-flops produces five non-overlapping machine phases, and
            each instruction advances through them one by one. The distinct clock strobes used across
            the datapath — reading the register file, latching the ALU result, writing the result
            back and updating the program counter — are all derived from these phases.
        </p>
        <img className='singleimage' src={sequencer} alt='Five-phase clock sequencer' />
        <div className='singleimagedescription'>Five-phase clock sequencer (ring counter)</div>

        <GlowingText2em content='Instruction set' />
        <p className='projecttext'>
            The table below lists the fourteen instructions currently implemented. Arithmetic comes
            in two flavours — a register-register form (add, sub, mul, div) and an immediate form
            (addi, subi, muli, divi). Beyond arithmetic, the machine has a comparison (slt),
            memory access (lw, sw), a conditional branch (beqz), an unconditional jump to an
            immediate address (j) and a register-indirect jump (jr, which loads the program counter
            from a register) — together enough to express real control flow, from loops to jump
            tables and subroutine returns.
        </p>

        <div className='proc-table-wrap'>
            <table className='proc-table'>
                <thead>
                    <tr><th>Opcode</th><th>Mnemonic</th><th>Operands</th><th>Operation</th></tr>
                </thead>
                <tbody>
                    {ISA.map(([op, mn, args, desc]) => (
                        <tr key={op}>
                            <td><code>{op}</code></td>
                            <td><code>{mn}</code></td>
                            <td>{args}</td>
                            <td className='proc-desc'>{desc}</td>
                        </tr>
                    ))}
                </tbody>
                <caption>Instruction set (slt sets RD to 1 when RX &lt; RY, else 0).</caption>
            </table>
        </div>

        <div className="subtext"><GlowingText1em content='Control signals' /></div>
        <p className='projecttext'>
            Internally, the control unit decodes each opcode into a small set of control lines that
            steer the datapath for that instruction. The table below is exactly that decode — the
            truth table wired into the control unit — with the asserted (1) signals highlighted:
        </p>
        <div className='projecttext'>
            <ul>
                <li><b>Jump</b> — unconditionally load the program counter with the target (j; also jr).</li>
                <li><b>Branch</b> — with the ALU's ZERO flag, load the program counter with the target (beqz); asserted together with Jump it instead means jr (see below).</li>
                <li><b>MemWrite</b> — write a register into the data memory (sw).</li>
                <li><b>MemULA</b> — the write-back value comes from memory instead of the ALU (lw).</li>
                <li><b>ALUadr</b> — route the ALU output onto the memory-address / branch path (lw, sw, beqz).</li>
                <li><b>ULAop</b> — 3-bit selector for the ALU operation.</li>
                <li><b>WriteBack</b> — write the result into the destination register RD.</li>
                <li><b>ALUsrc</b> — the ALU's second operand is the immediate (1) or a register (0).</li>
            </ul>
        </div>
        <p className='projecttext'>
            One neat encoding trick: <code>jr</code> (opcode 0xF) needs no new control line. It simply
            asserts <b>Jump</b> and <b>Branch</b> <i>at the same time</i> — a combination no other opcode
            produces — and the next-PC logic reads that pair as "take the jump target from register RX"
            instead of from the instruction's immediate. So the register-indirect jump falls out of the
            two jump signals the machine already had.
        </p>

        <div className='proc-table-wrap'>
            <table className='proc-table proc-compact'>
                <thead>
                    <tr>
                        <th>Opcode</th>
                        <th>Mnemonic</th>
                        {CONTROL_COLS.map((c) => <th key={c}>{c}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {CONTROL.map((row) => {
                        const op = row[0];
                        const mn = row[1];
                        const signals = row.slice(2, 10);
                        return (
                            <tr key={op}>
                                <td><code>{op}</code></td>
                                <td><code>{mn}</code></td>
                                {signals.map((v, i) => (
                                    <td key={i} className={v === '1' ? 'proc-on' : undefined}>{v}</td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
                <caption>Control word per opcode, as decoded by the control unit (ULA op is the 3-bit ALU operation).</caption>
            </table>
        </div>

        <GlowingText2em content='Execution: five machine phases' />
        <p className='projecttext'>
            Each instruction is carried out over the same five phases. Instructions that do not need
            a given stage still pass through it — a jump, for example, produces its effect when the
            program counter is updated rather than by touching memory. The phases are:
        </p>

        <div className='proc-table-wrap'>
            <table className='proc-table'>
                <thead>
                    <tr><th>Phase</th><th>Name</th><th>What happens</th></tr>
                </thead>
                <tbody>
                    {PHASES.map(([n, name, what]) => (
                        <tr key={n}>
                            <td><code>{n}</code></td>
                            <td>{name}</td>
                            <td className='proc-desc'>{what}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>

        <GlowingText2em content='Simulation in Logisim' />
        <ToolChips tools={['Logisim']} />
        <p className='projecttext'>
            The whole processor runs inside Logisim, where a program is loaded into the instruction
            memory as a raw memory image. A small demonstration program builds a nested loop — a
            <b> for </b> inside a <b> while </b> — using only set-less-than to compare a counter,
            branch-if-zero to test the loop condition, and jump to close the loop. Watching the
            program counter step through the instructions and the registers update phase by phase is
            the clearest way to see the architecture actually computing.
        </p>
        <p className='projecttext'>
            The demonstration program occupies the first seven words of the instruction memory:
        </p>
        <div className='proc-table-wrap'>
            <table className='proc-table'>
                <thead>
                    <tr><th>Address</th><th>0</th><th>1</th><th>2</th><th>3</th><th>4</th><th>5</th><th>6</th></tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Word (hex)</td>
                        <td><code>0000</code></td>
                        <td><code>0112</code></td>
                        <td><code>0221</code></td>
                        <td><code>8312</code></td>
                        <td><code>9032</code></td>
                        <td><code>0240</code></td>
                        <td><code>e003</code></td>
                    </tr>
                </tbody>
                <caption>The nested-loop demo, as a Logisim "v2.0 raw" memory image.</caption>
            </table>
        </div>
        <div className='proc-downloads'>
            <DownloadLink file='loop.mem' label='Demo program (loop.mem)' />
            <DownloadLink file='Processor.circ' label='Logisim circuit (Processor.circ)' />
        </div>
        <p className='projecttext'>
            With the field order confirmed — the opcode is the most-significant nibble — those seven
            words decode into a compact nested loop. The opcodes this program uses are <code>0</code> =
            <code>addi</code>, <code>8</code> = <code>slt</code>, <code>9</code> = <code>beqz</code> and
            <code>E</code> = <code>j</code>:
        </p>
        <div className='proc-table-wrap'>
            <table className='proc-table'>
                <thead>
                    <tr><th>Addr</th><th>Word</th><th>Assembly</th><th>What it does</th></tr>
                </thead>
                <tbody>
                    <tr><td><code>0</code></td><td><code>0000</code></td><td><code>addi r0, r0, 0</code></td><td className='proc-desc'>no-op (adds 0 to r0)</td></tr>
                    <tr><td><code>1</code></td><td><code>0112</code></td><td><code>addi r1, r1, 2</code></td><td className='proc-desc'>r1 = 2 — the loop bound (runs once)</td></tr>
                    <tr><td><code>2</code></td><td><code>0221</code></td><td><code>addi r2, r2, 1</code></td><td className='proc-desc'>r2 = r2 + 1 — inner counter &nbsp;←&nbsp; branch target</td></tr>
                    <tr><td><code>3</code></td><td><code>8312</code></td><td><code>slt r3, r1, r2</code></td><td className='proc-desc'>r3 = (r1 &lt; r2) ? 1 : 0 &nbsp;←&nbsp; jump target</td></tr>
                    <tr><td><code>4</code></td><td><code>9032</code></td><td><code>beqz r3, 2</code></td><td className='proc-desc'>if r3 == 0, go back to 2 (keep counting)</td></tr>
                    <tr><td><code>5</code></td><td><code>0240</code></td><td><code>addi r2, r4, 0</code></td><td className='proc-desc'>r2 = 0 — reset the counter</td></tr>
                    <tr><td><code>6</code></td><td><code>e003</code></td><td><code>j 3</code></td><td className='proc-desc'>restart the loop — forever</td></tr>
                </tbody>
                <caption>loop.mem decoded: a <b>for</b> (addresses 2–4, counting r2 up until it passes r1) nested inside a <b>while</b> (the jump at 6 that never ends).</caption>
            </table>
        </div>
        <p className='projecttext'>
            Running that program brings the whole datapath to life. The clip below is the original
            Logisim schematic — the very design later rewritten module by module in SystemVerilog —
            executing the nested-loop program: bright-green wires are carrying a logic 1, the program
            counter (the D-Q register on the left) steps through the instruction memory, and the ALU,
            register file and multiplexers pass values around the loop.
        </p>
        <video className='singleimage' src={process.env.PUBLIC_URL + '/processor/logisim_processor.mp4'}
               autoPlay loop muted playsInline controls />
        <div className='singleimagedescription'>The processor running in Logisim — two full passes of the nested-loop program</div>
        <p className='projecttext'>
            Watching it run makes the multicycle rhythm visible: the program counter advances, each
            instruction word highlights in the instruction memory as it is fetched, and the
            seven-segment output at the bottom updates as the loops count — two complete passes of the
            program in this clip. Everything that follows on this page is the work of turning exactly
            this behaviour into synthesizable hardware, one block at a time.
        </p>

        <GlowingText2em content='From schematic to FPGA' />
        <ToolChips tools={['SystemVerilog', 'ModelSim', 'Yosys + netlistsvg', 'Quartus II', 'DE2-115 (Cyclone IV E)']} />
        <p className='projecttext'>
            A Logisim schematic is perfect for understanding a design, but it cannot be synthesized
            onto an FPGA directly. To run the processor on real silicon — the Altera/Intel DE2-115
            board, built around a Cyclone IV E device — each block is being rewritten by hand in
            synthesizable SystemVerilog. The work is done module by module: every block gets its own
            RTL, a self-checking testbench that proves it in simulation, and a small demo that shows
            it working on the board before it is integrated into the full processor.
        </p>
        <p className='projecttext'>
            Each block below is its own self-contained subtopic — its generated circuit, its
            simulation waveform, and its FPGA footprint — building up to the datapath integration,
            which begins right after the modules with the fetch stage.
        </p>

        <div className="subtext"><GlowingText1em content='ALU' /></div>
        <p className='projecttext'>
            The first module brought up this way is the ALU. Its SystemVerilog description is purely
            combinational — the result is a function of the two operands and the operation code —
            which makes it easy to reason about and to verify exhaustively:
        </p>

        <pre id="codeblock">
            <CodeBlock code={aluVerilog} language="verilog" />
        </pre>
        <GitLink file='rtl/alu.sv' />

        <p className='projecttext'>
            Elaborating that SystemVerilog yields the gate / mux / register-level circuit below,
            generated straight from the code with Yosys and netlistsvg — the same kind of view the
            Quartus RTL Viewer shows, i.e. the actual logic the description compiles into.
        </p>
        <img className='singleimage' src={aluRtl} alt='ALU RTL logic circuit' />
        <div className='singleimagedescription'>ALU logic circuit, generated from the RTL</div>

        <p className='projecttext'>
            A self-checking testbench drives directed and pseudo-random operands and compares every
            result against a reference model — over two thousand checks pass with zero mismatches in
            ModelSim. The waveform below shows one operation per step, each with the correct result
            and the ZERO flag behaving as expected.
        </p>
        <img className='singleimage' src={aluWave} alt='ALU simulation waveform' />
        <div className='singleimagedescription'>ALU waveform — add, sub, mul, div, slt and beqz, with results and the ZERO flag</div>

        <p className='projecttext'>
            One honest note the circuit above makes visible: multiply and divide appear as single
            <code>$mul</code> / <code>$div</code> blocks, and in Logisim they resolve in a single
            simulation step — but real hardware cannot. On the Cyclone IV E the multiplier maps to
            dedicated <b>DSP blocks</b>, so it stays fast; the divider, kept fully combinational here,
            becomes a long chain of logic with a slow propagation delay — which is exactly why a
            production design turns division into a <b>multi-cycle / iterative</b> unit. It is left
            combinational for now to keep this first module simple to bring up and test.
        </p>
        <p className='projecttext'><b>Synthesis (EP4CE115F29C7):</b> 387 logic elements, 2 DSP blocks (the multiplier), no registers.</p>
        <p className='projecttext'><b>Formally verified:</b> an exhaustive SAT proof (Yosys) confirms the ZERO flag, add / sub, set-less-than and divide-by-zero properties hold for <i>every</i> possible input — a mathematical guarantee, not a sample of tests.</p>

        <p className='projecttext'>
            Finally, on real hardware: the design is compiled in Intel Quartus Prime for the
            DE2-115's <code>EP4CE115F29C7</code> and programmed over JTAG, turning the ALU into an
            interactive calculator. The two operands are set on the slide switches (SW7-0 and SW14-8)
            and the operation on SW17-15, with the 16-bit result on the seven-segment displays and the
            ZERO / sign flags on the green LEDs. Swipe through each operation, captured live on the board:
        </p>
        <Gallery images={aluBoardImages} alt="ALU on the DE2-115" />

        {OTHER_MODULES.map((m) => (
            <div key={m.svg}>
                <div className="subtext"><GlowingText1em content={m.name} /></div>
                <p className='projecttext'>{m.blurb}</p>
                {m.code ? <pre id="codeblock"><CodeBlock code={m.code} language="verilog" /></pre> : null}
                {m.code ? <GitLink file={'rtl/' + m.svg + '.sv'} /> : null}
                {m.codePara ? <p className='projecttext'>{m.codePara}</p> : null}
                <img className='singleimage' src={rtlUrl(m.svg)} alt={m.name + ' logic circuit'} />
                <div className='singleimagedescription'>{m.name} — generated logic circuit</div>
                {m.circuitPara ? <p className='projecttext'>{m.circuitPara}</p> : null}
                <img className='singleimage' src={m.wave} alt={m.name + ' waveform'} />
                <div className='singleimagedescription'>{m.name} — simulation waveform</div>
                {m.wavePara ? <p className='projecttext'>{m.wavePara}</p> : null}
                <p className='projecttext'><b>Synthesis (EP4CE115F29C7):</b> {m.synth}</p>
                {m.formal ? <p className='projecttext'><b>Formally verified:</b> {m.formal}</p> : null}
                {m.closingPara ? <p className='projecttext'>{m.closingPara}</p> : null}
                {m.video ? <p className='projecttext'>{m.videoIntro}</p> : null}
                {m.video ? <video className='singleimage' src={m.video} autoPlay loop muted playsInline controls /> : null}
                {m.video ? <div className='singleimagedescription'>{m.videoCaption}</div> : null}
                {m.video ? <p className='projecttext'>{m.videoPara}</p> : null}
                {m.board ? <p className='projecttext'>{m.boardIntro}</p> : null}
                {m.board ? <Gallery images={m.board} alt={m.name + ' on the DE2-115'} /> : null}
            </div>
        ))}

        <GlowingText2em content='First integration: the fetch stage' />
        <ToolChips tools={['SystemVerilog', 'Quartus II', 'DE2-115']} />
        <p className='projecttext'>
            With every block working on its own, the first step of wiring them together is the
            <b> fetch stage</b>: the program counter feeds the instruction memory. The PC produces an
            address and the memory hands back the instruction stored there — so as the counter advances,
            the program streams out one instruction at a time. That single connection, the PC's output
            driving the memory's address, is the whole idea of instruction fetch — the very first thing a
            processor does in every cycle.
        </p>
        <pre id="codeblock"><CodeBlock code={fetchVerilog} language="verilog" /></pre>
        <GitLink file='fpga/de2_115/fetch_top.sv' />
        <p className='projecttext'>
            On the DE2-115 this runs as a self-contained demo: a slow clock, divided down from the 50 MHz
            oscillator, advances the PC about twice a second. The four right-hand displays show the 16-bit
            instruction fetched at the current address — one hex digit per instruction field
            (OP · RD · RX · RY/I) — while the leftmost display shows the address itself. A switch flips
            between this automatic run and single-stepping the fetch by hand, one instruction per button
            press.
        </p>
        <video className='singleimage' src={process.env.PUBLIC_URL + '/processor/fetch_pc_imem.mp4'}
               autoPlay loop muted playsInline controls />
        <div className='singleimagedescription'>Fetch on the DE2-115 — the PC walks the program and the instruction memory returns each word</div>
        <p className='projecttext'>
            Watch the address on the left climb 0, 1, 2 … F and wrap back to 0 while the instruction on the
            right changes to match: the seven-word demo program appears at addresses 0 through 6 — a green
            LED marks every address that holds a real instruction — and then empties out to 0000 for the
            rest, so you can literally see where the program ends. This is the processor fetching: the same
            loop that, once the remaining stages are wired in, will decode and execute each of these words.
        </p>
        <p className='projecttext'>
            One design choice this makes concrete: instructions and data live in <b>separate</b> memories
            here — a small read-only instruction ROM and a large read/write data RAM — which is the
            <b> Harvard</b> style, as opposed to the <b>von Neumann</b> approach of a single memory holding
            both. In a multicycle machine like this one they could in fact share one memory (fetch and
            data access happen in different phases, so they never collide), but keeping them apart is
            simpler to build and to reason about — and it mirrors how real processors split their fast L1
            cache into separate instruction and data halves.
        </p>

        <GlowingText2em content='The complete processor' />
        <ToolChips tools={['SystemVerilog', 'ModelSim', 'Quartus II', 'DE2-115']} />
        <p className='projecttext'>
            With every block verified on its own and the fetch stage joined, the last step wires the
            whole datapath into one module, <code>cpu.sv</code>: the five-phase ring sequencer conducts
            the program counter, instruction memory, register file and ALU through one instruction at a
            time, and the machine runs the original <code>loop.mem</code> from end to end. Each block acts
            only in the phase that owns it, so the whole processor is one clean synchronous machine.
        </p>
        <pre id="codeblock"><CodeBlock code={cpuVerilog} language="verilog" /></pre>
        <GitLink file='rtl/cpu.sv' />
        <p className='projecttext'>
            The phases do their work in order: <b>fetch</b> and <b>decode</b> are combinational (the PC
            addresses the ROM, the opcode raises its control lines, and the register file reads its
            sources), the ALU result and the ZERO flag are latched in the <b>execute</b> phase, and the
            <b> write-back</b> phase both stores the result into the destination register and commits the
            next PC — either PC + 1, or a jump / taken-branch target.
        </p>
        <img className='singleimage' src={waveCpu} alt='Integrated CPU simulation waveform' />
        <div className='singleimagedescription'>The integrated CPU running loop.mem in simulation</div>
        <p className='projecttext'>
            The waveform makes the nested loop visible in the control flow itself: the program counter
            walks 0, 1, 2 into the loop, then cycles 2 → 3 → 4 while the inner counter climbs (the branch
            keeps jumping back to 2); once the comparison finally fails it falls through to 5 → 6 and
            jumps back to 3 to start over — with the fetched instruction word tracking the PC the whole
            way. A self-checking testbench runs 45 instructions and confirms the register trace and every
            branch and jump with zero mismatches in ModelSim.
        </p>
        <p className='projecttext'><b>Synthesis (EP4CE115F29C7):</b> 317 logic elements, 134 registers. Because the demo program is fixed in the instruction ROM, the synthesizer prunes the datapath it can prove this program never exercises — the idle data memory and the unused multiply / divide units — leaving just the logic the loop actually drives.</p>
        <p className='projecttext'>
            Finally, the whole processor was brought up on the DE2-115 with a slow, steppable clock so the
            five phases and the loop can be watched by eye — the entire design, from hand-drawn Logisim
            schematic to synthesizable SystemVerilog, executing its own program on real hardware:
        </p>
        <video className='singleimage' src={process.env.PUBLIC_URL + '/processor/cpu_board.mp4'}
               autoPlay loop muted playsInline controls />
        <div className='singleimagedescription'>The complete processor on the DE2-115 running loop.mem — PC on HEX7, opcode on HEX5, and r1 · r2 · r3 on the right</div>
        <p className='projecttext'>
            Everything moves at once: the program counter (HEX7) jumps around the loop, the machine phase
            marches across the green LEDs, and the register values on the right tell the story — r2, the
            loop counter, climbs 1, 2, 3 and resets to 0, while r3 flicks to 1 at the exact moment the
            comparison sends the loop back to the top. It is the same behaviour proven in the waveform
            above, now running on real silicon — the payoff of building the machine up one verified block
            at a time.
        </p>

        <GlowingText2em content='Formal verification' />
        <ToolChips tools={['Yosys (SAT)', 'MiniSat']} />
        <p className='projecttext'>
            Simulation shows the design works for the inputs a testbench happens to try. Formal
            verification answers the stronger question: is a property true for <i>every</i> possible
            input and state? A solver treats the inputs as symbolic — they can be anything — and
            searches for any assignment that would break the property, proving none can exist or handing
            back a concrete counterexample. Every one of the seven leaf modules here carries such a proof,
            run by Yosys's built-in SAT engine with no external solver:
        </p>
        <pre id="codeblock"><CodeBlock code={formalSuiteOutput} language="text" /></pre>
        <GitLink file='tools/run_formal.ps1' />
        <p className='projecttext'>
            The proofs split by module type. The three <b>combinational</b> blocks — ALU, control unit
            and instruction ROM — are proved <b>exhaustively</b>: with no state to unroll, a single SAT
            query covers every input at once — every operand pair and <code>ULAOP</code> for the ALU, all
            16 opcodes for the decoder, all 16 addresses for the ROM. The four <b>sequential</b> blocks —
            program counter, register file, data memory and sequencer — are proved <b>unbounded by
            k-induction</b>, which closes the "only checked up to depth <i>k</i>" gap that bounded model
            checking leaves: it proves that if the invariant holds for <i>k</i> consecutive cycles it must
            hold on the next, so the guarantee covers all time and all input sequences.
        </p>
        <p className='projecttext'>
            That last part is worth seeing directly. Below, the solver proves the sequencer can never leave
            its legal one-hot state: it first tries an induction length of 1, finds the step is not yet
            provable, lengthens to 2, and closes the proof.
        </p>
        <pre id="codeblock"><CodeBlock code={formalInductionLog} language="text" /></pre>
        <p className='projecttext'>
            <b>Base case … proven</b> followed by <b>Induction step proven: SUCCESS!</b> is the whole game:
            the invariant holds at power-on, and every legal state leads only to another legal state — so the
            one-hot phase can never glitch into a stuck or multi-hot code, on any run of any length. That is
            a guarantee no amount of simulation can give. The full set of properties lives in the
            repository's <code>formal/</code> directory and reruns with a single script.
        </p>

        <GlowingText2em content='Resource usage on the FPGA' />
        <ToolChips tools={['Quartus II']} />
        <p className='projecttext'>
            Each module was synthesized on its own for the DE2-115's Cyclone IV E
            (<code>EP4CE115F29C7</code>). A few things stand out: the register file's 256 flip-flops
            are exactly 16 registers × 16 bits; the data memory's 1 Mbit maps to on-chip block RAM —
            about a quarter of the device; and only the ALU needs DSP blocks, for the multiplier.
        </p>
        <div className='proc-table-wrap'>
            <table className='proc-table'>
                <thead>
                    <tr>
                        <th>Module</th>
                        <th>Logic elements</th>
                        <th>Registers</th>
                        <th>Memory bits</th>
                        <th>DSP (9-bit)</th>
                    </tr>
                </thead>
                <tbody>
                    {SYNTH.map((r) => (
                        <tr key={r[0]}>
                            <td>{r[0]}</td><td>{r[1]}</td><td>{r[2]}</td><td>{r[3]}</td><td>{r[4]}</td>
                        </tr>
                    ))}
                </tbody>
                <caption>Standalone synthesis on the EP4CE115F29C7 — device has 114,480 logic elements, 3,981,312 memory bits and 532 DSP elements.</caption>
            </table>
        </div>

        <GlowingText2em content='Video output: a bouncing ball the CPU computes' />
        <ToolChips tools={['SystemVerilog', 'ModelSim', 'Yosys (SAT)', 'Quartus II', 'DE2-115']} />
        <p className='projecttext'>
            The final application puts the whole processor to work driving a real display: through the
            DE2-115's VGA output the CPU paints the screen, and it does the interesting part itself — it
            <i> computes what to draw.</i> It arrives there in two steps. First a simple sliding square,
            just to prove the memory-mapped video path end to end; then the real thing — a tile-based
            <b> GPU</b> rendering a ball that falls and bounces under gravity, losing a little energy on
            every bounce, all worked out by the CPU's own arithmetic and painted a cell at a time.
        </p>

        <div className="subtext"><GlowingText1em content='The sliding square' /></div>
        <p className='projecttext'>
            The first version slides a square left and right — a real little physics loop in thirteen
            instructions. It adds a step to the X position, checks whether it has reached either wall,
            and if so flips the direction, then stores the new X to a memory-mapped video address. That
            is a genuine bounce, worked out with the CPU's own add, subtract and branch instructions:
        </p>
        <pre id="codeblock"><CodeBlock code={vgaProgram} language="text" /></pre>
        <GitLink file='rtl/imem.sv' />
        <p className='projecttext'>
            Each store to the video address is snooped into a video register. Because the CPU has already
            done the thinking, the hardware has almost nothing left to do: it runs its own 640×480 @ 60 Hz
            raster — a pair of counters generating the horizontal and vertical sync a monitor locks onto —
            and, for each pixel, simply tests whether it falls inside the 64×64 square at the X the CPU
            chose (the vertical position is fixed at the centre, so the square slides left and right).
            There is no arithmetic in the hardware at all — the motion lives entirely in the program:
        </p>
        <pre id="codeblock"><CodeBlock code={vgaRender} language="verilog" /></pre>
        <GitLink file='rtl/vga_square.sv' />
        <p className='projecttext'>
            Running that RTL in simulation and capturing one whole frame renders exactly what the monitor
            would show — a black square on a white field, here at the position for counter value 200:
        </p>
        <img className='singleimage' src={process.env.PUBLIC_URL + '/processor/vga_output.png'} alt='Simulated VGA output: a black square on a white field' />
        <div className='singleimagedescription'>One 640×480 frame, rendered straight from the RTL in simulation</div>
        <p className='projecttext'>
            The timing generator is <b>formally verified</b> too: a k-induction proof shows the raster
            counters can never leave their 800×525 grid and that the visible-area flag exactly matches the
            640×480 window — so the sync a monitor depends on can never drift. That same raster, and the
            same memory-mapped store, carry straight over into the real application below — only what the
            CPU computes, and how the hardware reads it back, get richer.
        </p>

        <div className="subtext"><GlowingText1em content='From a square to a tile GPU' /></div>
        <p className='projecttext'>
            The sliding square proved the CPU could turn its memory-mapped output into pixels. The real
            application turns that into a proper little <b>GPU</b>: a tile framebuffer the processor
            draws into, and a program that computes real gravity — so the square becomes a ball that
            falls, bounces, and <i>loses energy on every bounce</i> like a real one, all worked out by
            the CPU's own arithmetic and painted a cell at a time. The CPU is the physics, the GPU is
            the pixels, and a framebuffer sits between them.
        </p>

        <div className="subtext"><GlowingText1em content='The framebuffer GPU' /></div>
        <p className='projecttext'>
            Instead of one hard-wired square, the video stage becomes a <b>tile framebuffer</b>. The
            screen is a 40 × 30 grid of 16 × 16-pixel cells, and each cell holds a colour index in a
            small dual-port RAM (the <code>tileram</code>). For every pixel the raster draws, the GPU
            works out which cell it belongs to — because the cells are a power-of-two wide, the divide
            is just a bit-slice — reads that cell's colour, and paints it. The CPU "draws" purely by
            storing a colour index into a cell through the memory-mapped <code>sw</code>, exactly the
            way a real display adapter's framebuffer works. Keeping the read <b>registered</b> is what
            lets the whole grid map onto the FPGA's on-chip block RAM instead of a mountain of logic.
        </p>
        <pre id="codeblock"><CodeBlock code={gpuRender} language="verilog" /></pre>
        <GitLink file='rtl/vga_gpu.sv' />

        <div className="subtext"><GlowingText1em content='The physics, in software' /></div>
        <p className='projecttext'>
            All of the motion lives in the program. The ball's vertical position is kept in
            fixed-point — <code>fine_y</code>, in eighths of a cell, so the tile row to light is just
            <code> fine_y / 8</code>. Each frame the CPU adds gravity to the velocity, moves the ball,
            and when it reaches the floor, bounces it. To make the bounce shed energy like a real
            ball, the relaunch speed comes from an <b>energy register</b> <code>E</code> that drops by
            one each bounce — so every bounce is lower than the last — and when the energy runs out the
            demo resets to a fresh drop from the top and loops forever. There is not a single branch
            inside the loop: this ISA's <code>beqz</code> only reaches the first sixteen addresses and
            the program lives above them, so every "if" is done <b>branchlessly</b>, with
            <code> slt</code> producing a 0/1 mask that is multiplied in.
        </p>
        <pre id="codeblock"><CodeBlock code={gravAlgo} language="text" /></pre>
        <p className='projecttext'>
            That whole per-frame update is nothing but add, subtract, multiply, set-less-than and one
            divide — the same instructions verified far above, now expressing Newtonian motion.
        </p>

        <div className="subtext"><GlowingText1em content='The program, instruction by instruction' /></div>
        <p className='projecttext'>
            Here is the whole thing the CPU actually runs to bounce the ball — the real program in the
            machine's own assembly. The setup builds the constants (the immediate field is only four
            bits, so 40, 224 and 1600 are multiplied up from small values) and clears the state; then
            the frame loop does gravity, the energy-losing bounce, the reset, and the flicker-free draw
            before jumping back to the top. Each register's job is noted inline — it is the pseudocode
            above, made concrete:
        </p>
        <pre id="codeblock"><CodeBlock code={gravProgramFull} language="text" /></pre>
        <GitLink file='rtl/imem.sv' />

        <div className="subtext"><GlowingText1em content='Getting there: two bugs, found by simplifying' /></div>
        <p className='projecttext'>
            The first version did not look like that at all. It <b>flickered</b> badly and, worse, the
            ball would sometimes fall off the bottom and reappear at the top. Getting to a clean bounce
            was the real work, and it came down to isolating two separate bugs — stripping the demo
            down to a static pattern, then one fixed cell, then a single ball, until each cause showed
            itself:
        </p>
        <div className='projecttext'>
            <ul>
                <li>
                    <b>The wrap-around was integer drift.</b> The early bounce simply reflected the
                    velocity (<code>vy = −vy</code>), but integer rounding made each bounce come out a
                    touch different until the position slowly walked off the grid. The fix makes the
                    bounce <i>deterministic</i>: clamp the position exactly onto the floor and set the
                    velocity to an exact value, so the ball can never drift — it stays on the grid
                    <i> by construction</i>. That is exactly the energy-register scheme above.
                </li>
                <li>
                    <b>The flicker was a timing gap.</b> The loop erased the old cell and <i>then</i>
                    drew the new one, so for the one instruction in between, the ball was blank — and
                    the monitor kept catching it mid-erase. The fix draws the new cell first and only
                    then erases the old, so the ball is never absent; and it <b>skips the erase entirely
                    when the ball has not changed row</b>, redirecting it to an off-screen scratch cell,
                    so a ball hovering near its apex is written once and left perfectly still.
                </li>
            </ul>
        </div>
        <p className='projecttext'>
            Both fixes are the last handful of instructions in the program above: the branchless
            <code> moved</code> mask that redirects the erase to the off-screen <code>SCRATCH</code>
            cell when the row is unchanged, and the two stores in <i>draw-then-erase</i> order.
        </p>

        <div className="subtext"><GlowingText1em content='Making the motion smooth' /></div>
        <p className='projecttext'>
            With the bounce correct, the last thing to fix was that the ball still moved in visible
            jumps and seemed to freeze at the top. That was two things at once. The tiles were too
            coarse, so each step was a big <b>teleport</b>; and, less obviously, the CPU clock was so
            slow the physics only advanced about <b>nine times a second</b>. Making the grid finer
            (16-pixel cells, 40 × 30) shrank each step, and speeding the CPU clock raised the update
            rate to nearly <b>forty times a second</b> — together enough to read as continuous motion, with
            the ball now visibly <i>decelerating</i> through a thin band near the top instead of
            sitting frozen on one fat row.
        </p>

        <div className="subtext"><GlowingText1em content='Verified, then run on the board' /></div>
        <p className='projecttext'>
            The program is held to the same standard as every module here. A self-checking ModelSim
            testbench runs the CPU for tens of thousands of cycles alongside a small model of the
            framebuffer, and asserts the invariants that actually matter: the ball always stays on the
            grid, its successive bounces really do get lower (energy is lost), the demo resets and
            loops, and — the flicker property, checked directly — the erase never clears the cell the
            ball was just drawn into. All pass with zero errors.
        </p>
        <p className='projecttext'><b>Synthesis (EP4CE115F29C7):</b> 1,065 logic elements, 341 registers, 49,152 memory bits (the tile framebuffer plus the CPU's data memory), and 2 DSP blocks — the multiplier the physics leans on.</p>
        <p className='projecttext'>
            The most satisfying part is what <i>did not</i> change: the processor itself. The very same
            <code> cpu.sv</code> that ran the nested loop at the top of this page now runs the physics —
            only the program in the instruction ROM and the video peripheral wired around it are
            different. A general-purpose CPU earns its name exactly here: swap the software, keep the
            silicon, and the same machine bounces a ball.
        </p>
        <video className='singleimage' src={process.env.PUBLIC_URL + '/processor/grav_ball.mp4'}
               autoPlay loop muted playsInline controls />
        <div className='singleimagedescription'>The energy-losing bouncing ball on the DE2-115 — computed by the CPU, drawn into the tile framebuffer, out through VGA</div>
        <p className='projecttext'>
            On the monitor the whole scheme comes together: the ball drops from the top, bounces high,
            and each bounce peaks a little lower than the one before as the energy register counts
            down — a real decaying bounce — until the energy is spent and it resets to a fresh drop and
            begins again. It moves smoothly and never flickers, and every pixel of it traces back to
            integer arithmetic running on a processor built here from gates up.
        </p>

        <GlowingText2em content='Conclusion' />
        <p className='projecttext'>
            Building a processor from the ground up — from individual logic gates in a simulator all
            the way to synthesizable hardware on an FPGA — ties together digital logic, computer
            architecture, hardware description languages and the practical realities of a real
            development board. Each layer reveals something the previous one hid: the schematic
            shows the structure, the simulation shows the behaviour, and the FPGA forces the design
            to confront timing and physical constraints. The project is an ongoing, hands-on way to
            understand exactly what happens between pressing "run" and a result appearing.
        </p>

    </div>
);
